import React, { lazy } from "react";
import ApprovalPage from "./pages/ApprovalPage";
import DataEntryPage from "./pages/DataEntryPage";
import DataTablePage from "./pages/DataTablePage";
import EnterpriseTablePage from "./pages/EnterpriseTablePage";
import ExpenseManagementPage from "./pages/ExpenseManagementPage";
import ProfilePage from "./pages/ProfilePage";
import SchedulerPage from "./pages/SchedulerPage";
import ReportSchedulerPage from "./pages/ReportSchedulerPage";
import { Navigate } from "react-router-dom";
import DashboardErrorBoundary from "./components/errors/DashboardErrorBoundary";
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const SettingsPageV2 = lazy(() => import("./pages/SettingsPageV2"));
const ShopSettingsPanel = lazy(() => import("./pages/settings/ShopSettingsPanel"));
const AdminDashboard = lazy(() => import("./pages/settings/AdminDashboard"));
// Import the reports page with both basic and advanced tables
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
// Import the advanced table page (for direct access)
const DataTablePageAdvanced = lazy(() => import("./pages/DataTablePageAdvanced"));
const SettingsV3Page = lazy(() => import("./pages/settings/SettingsV3Page"));

const TaskDetailsPage = lazy(() => import("./pages/TaskDetailsPage"));
const TaskCreatePage = lazy(() => import("./pages/TaskCreatePage"));
const TaskListPage = lazy(() => import("./pages/TaskListPage"));
// Import Stock Management Components
const StockUsagePage = lazy(() => import("./pages/stock/StockUsagePage"));
const StockAuditPage = lazy(() => import("./pages/stock/StockAuditPage"));

// NEW Production Module (Epic: Mill Production Tracking & Audit)
const DailyProductionPage = lazy(() => import("./pages/production/DailyProductionPage"));
const ProductionAuditPage = lazy(() => import("./pages/production/ProductionAuditPage"));
const AuditManagementPage = lazy(() => import("./pages/production/AuditManagementPage"));
const ProductionManagementPage = lazy(() => import("./pages/production/ProductionManagementPage"));

// Legacy Production Pages - Removed (replaced by new Production Management system)

// ALL DASHBOARD IMPORTS REMOVED - MOVED TO LEGACY

// NEW DASHBOARD SYSTEM - Phase 1 Implementation
const WorkflowDashboardPage = lazy(() => import("./pages/dashboards/WorkflowDashboardPage"));
const WorkflowDashboard = lazy(() => import("./pages/dashboards/workflow-v3/WorkflowDashboard"));
const AnalyticsDashboardPage = lazy(() => import("./pages/dashboards/AnalyticsDashboardPage"));

// Analytics Dashboard V3 - Unified Analytics System
const AnalyticsDashboardV3 = lazy(() => import("./pages/dashboards/analytics-v3/AnalyticsDashboard"));

// Phase 3 Advanced Analytics Dashboard
const Phase3AnalyticsDashboard = lazy(() => import("./pages/Phase3AnalyticsDashboard"));
const Phase3AccessPage = lazy(() => import("./pages/Phase3AccessPage"));

// Audit Log Viewer (Phase 2.7)
const AuditLogPage = lazy(() => import("./pages/AuditLogPage"));

// Phase 3.1 - Visual Indicators Showcase
const IndicatorsShowcase = lazy(() => import("./pages/IndicatorsShowcase"));

// UI/UX Codex Demo Pages
const UIKitDemoPage = lazy(() => import("./pages/UIKitDemoPage"));
const AppShellDemoPage = lazy(() => import("./pages/AppShellDemoPage"));
const FormSystemDemoPage = lazy(() => import("./pages/FormSystemDemoPage"));
const DataTableDemoPage = lazy(() => import("./pages/DataTableDemoPage"));

// UI Component Test Page (for testing icon overlap and positioning)
const UIComponentTest = lazy(() => import("./pages/test/UIComponentTest"));
const SimpleIconTest = lazy(() => import("./pages/test/SimpleIconTest"));

// User Management - NEW DEFAULT UI
const UserManagementPage = lazy(() => import("./pages/UserManagementPage"));

// Dashboard V2 - Production (New Implementation)
const GlobalDashboard = lazy(() => import("./pages/dashboards/v2/GlobalDashboard"));
const MillTypeDashboard = lazy(() => import("./pages/dashboards/v2/MillTypeDashboard"));
const VehicleTypeDashboard = lazy(() => import("./pages/dashboards/v2/VehicleTypeDashboard"));
const NaturesDashboard = lazy(() => import("./pages/dashboards/v2/NaturesDashboard"));
const AdminOperationsDashboard = lazy(() => import("./pages/dashboards/v2/AdminOperationsDashboard"));
const UnitDashboard = lazy(() => import("./pages/dashboards/v2/UnitDashboard"));

export interface AppRoute {
  path: string;
  element: React.ReactNode;
  /** Roles allowed to access this route */
  rolesAllowed?: string[];
}

// Helper function to wrap dashboard components with error boundary
const withErrorBoundary = (Component: React.ReactNode, dashboardName: string) => (
  <DashboardErrorBoundary dashboardName={dashboardName}>
    {Component}
  </DashboardErrorBoundary>
);

export const appRoutes: AppRoute[] = [
  { path: "/entry", element: <DataEntryPage /> },
  // Legacy route redirects
  { path: "/data-entry-form", element: <Navigate to="/entry" replace /> },
  // Dashboard Routes - Canonical paths under /dashboards/* (with Error Boundaries)
  { path: "/dashboards/workflow", element: withErrorBoundary(<WorkflowDashboardPage />, "Workflow Dashboard") },
  { path: "/dashboards/workflow-v3", element: withErrorBoundary(<WorkflowDashboard />, "Workflow Dashboard V3") },
  { path: "/dashboards/analytics", element: withErrorBoundary(<AnalyticsDashboardPage />, "Analytics Dashboard"), rolesAllowed: ["admin", "superadmin", "manager"] },
  { path: "/dashboards/analytics-v3", element: withErrorBoundary(<AnalyticsDashboardV3 />, "Analytics Dashboard V3"), rolesAllowed: ["admin", "superadmin", "manager", "misViewer", "approver"] },
  { path: "/dashboards/phase3", element: withErrorBoundary(<Phase3AnalyticsDashboard />, "Phase 3 Analytics Dashboard"), rolesAllowed: ["admin", "superadmin", "manager"] },
  // Legacy dashboard route redirects
  { path: "/dashboard-workflow", element: <Navigate to="/dashboards/workflow" replace /> },
  { path: "/dashboard-analytics", element: <Navigate to="/dashboards/analytics" replace /> },
  { path: "/dashboard-phase3", element: <Navigate to="/dashboards/phase3" replace /> },
  { path: "/phase3", element: <Navigate to="/dashboards/phase3" replace /> },

  // Reports & Data Tables
  { path: "/reports", element: <ReportsPage /> },
  { path: "/data-table-basic", element: <Navigate to="/reports" replace /> },
  { path: "/data-table-advanced", element: <Navigate to="/expenses/manage" replace /> },

  // Unified Expense Management Page
  { path: "/expenses/manage", element: <ExpenseManagementPage />, rolesAllowed: ["editor", "manager", "admin", "superadmin"] },

  // Legacy route redirects
  { path: "/enterprise-table", element: <Navigate to="/expenses/manage" replace /> },
  { path: "/data-table", element: <Navigate to="/expenses/manage" replace /> },

  // Approvals
  { path: "/approvals", element: <ApprovalPage />, rolesAllowed: ["admin", "manager"] },

  // Settings - Modern UI (V3 is canonical)
  { path: "/settings", element: <SettingsV3Page />, rolesAllowed: ["admin", "superadmin"] },
  { path: "/settings/admin-dashboard", element: <AdminDashboard />, rolesAllowed: ["admin", "superadmin"] },
  { path: "/settings/shops", element: <ShopSettingsPanel />, rolesAllowed: ["admin"] },
  // Legacy Settings routes (for backward compatibility)
  { path: "/settings-v1", element: <Navigate to="/settings" replace /> },
  { path: "/settings-v2", element: <Navigate to="/settings" replace /> },
  { path: "/settings-v3", element: <Navigate to="/settings" replace /> },

  // User Management - Modern UI (Default)
  { path: "/admin/users", element: <UserManagementPage />, rolesAllowed: ["admin", "superadmin"] },

  { path: "/stock/usage", element: <StockUsagePage />, rolesAllowed: ["admin", "editor", "user"] },
  { path: "/stock/audit", element: <StockAuditPage />, rolesAllowed: ["admin", "editor"] },

  // NEW Production Module Routes
  { path: "/production/daily", element: <DailyProductionPage />, rolesAllowed: ["admin", "editor", "user"] },
  { path: "/production/audit", element: <ProductionAuditPage />, rolesAllowed: ["admin", "editor"] },
  { path: "/production/audit/history", element: <AuditManagementPage />, rolesAllowed: ["admin", "superadmin", "auditor"] },
  { path: "/production/manage", element: <ProductionManagementPage />, rolesAllowed: ["admin", "editor", "user"] },

  // Legacy Production Routes (For testing - to be removed)
  // Legacy production routes removed - replaced by new Production Management system

  { path: "/tasks", element: <TaskListPage />, rolesAllowed: ["user", "editor", "manager", "admin", "superadmin"] },
  { path: "/tasks/new", element: <TaskCreatePage />, rolesAllowed: ["user", "editor", "manager", "admin", "superadmin"] },
  { path: "/tasks/:id", element: <TaskDetailsPage />, rolesAllowed: ["user", "editor", "manager", "admin", "superadmin"] },
  // Legacy Admin Panel - REMOVED (replaced by modern /admin/users)
  // { path: "/admin", element: <AdminPanelPage />, rolesAllowed: ["admin"] },

  { path: "/profile", element: <ProfilePage /> },
  // Audit Log Viewer (Phase 2.7 - System Audit Trail)
  { path: "/audit-logs", element: <AuditLogPage />, rolesAllowed: ["admin", "superadmin", "auditor"] },
  // Phase 3.1 - Visual Indicators Showcase (Development/Testing)
  { path: "/indicators-showcase", element: <IndicatorsShowcase />, rolesAllowed: ["admin", "superadmin"] },
  // UI/UX Codex Demo Pages (Development/Testing only - gated by environment)
  ...(import.meta.env.DEV || import.meta.env.VITE_IS_PREVIEW === 'true' ? [
    { path: "/demo/ui-kit", element: <UIKitDemoPage />, rolesAllowed: ["admin", "superadmin"] },
    { path: "/demo/app-shell", element: <AppShellDemoPage />, rolesAllowed: ["admin", "superadmin"] },
    { path: "/demo/form-system", element: <FormSystemDemoPage />, rolesAllowed: ["admin", "superadmin"] },
    { path: "/demo/data-table", element: <DataTableDemoPage />, rolesAllowed: ["admin", "superadmin"] },
    // UI Component Test Page
    { path: "/test/ui-components", element: <UIComponentTest /> },
    { path: "/test/simple-icons", element: <SimpleIconTest /> },
    // Dashboard V2 - Production Implementation (Real Firestore Data - with Error Boundaries)
    { path: "/dashboards/v2", element: withErrorBoundary(<GlobalDashboard />, "Global Dashboard V2"), rolesAllowed: ["admin", "superadmin", "manager"] },
    { path: "/dashboards/v2/global", element: withErrorBoundary(<GlobalDashboard />, "Global Dashboard V2"), rolesAllowed: ["admin", "superadmin", "manager"] },
    { path: "/dashboards/v2/type/mill", element: withErrorBoundary(<MillTypeDashboard />, "Mill Type Dashboard V2"), rolesAllowed: ["admin", "superadmin", "manager"] },
    { path: "/dashboards/v2/type/vehicle", element: withErrorBoundary(<VehicleTypeDashboard />, "Vehicle Type Dashboard V2"), rolesAllowed: ["admin", "superadmin", "manager"] },
    { path: "/dashboards/v2/natures", element: withErrorBoundary(<NaturesDashboard />, "Natures Dashboard V2"), rolesAllowed: ["admin", "superadmin", "manager"] },
    { path: "/dashboards/v2/admin", element: withErrorBoundary(<AdminOperationsDashboard />, "Admin Operations Dashboard V2"), rolesAllowed: ["admin", "superadmin", "manager"] },
    { path: "/dashboards/v2/unit/:unitId", element: withErrorBoundary(<UnitDashboard />, "Unit Dashboard V2"), rolesAllowed: ["admin", "superadmin", "manager"] },
  ] : []),
];

export const homeByRole: Record<string, string> = {
  superadmin: "/entry",
  admin: "/entry",
  manager: "/entry",
  auditor: "/entry",
  editor: "/entry",
  user: "/entry",
};
